import { addDays, format, isSameDay, max, startOfDay } from 'date-fns';
import { compact } from 'lodash-es';
import { getMaxFutureInvoiceDate } from '~/business-logic/billingLogic';
import { BillMode } from '~/components/BillModal/BillClientModal.types';
import { sendErrReport } from '~/infra/utils';
import { relationshipModel, timeModel } from '~/models';
export function getMinDateMessage(minDate) {
    return minDate ? "Date should not be before ".concat(format(minDate, 'LLL do, Y')) : undefined;
}
export function getMaxDateMessage(maxDate) {
    return maxDate ? "Date should not be after ".concat(format(maxDate, 'LLL do, Y')) : undefined;
}
export const selectorWidth = '320px';
export function getIssueDateBounds(now, earliestBillableDate) {
    return {
        minDate: earliestBillableDate,
        maxDate: getMaxFutureInvoiceDate(now)
    };
}
export const getCtaText = (params)=>{
    switch(params.mode){
        case BillMode.Now:
            return params.ctaTexts.billNow();
        case BillMode.Upcoming:
            return params.ctaTexts.addToBill();
        case BillMode.FutureDate:
            return params.ctaTexts.bill();
    }
};
export function getDateRange(mode, now, effectiveDate, earliestBillableDate) {
    switch(mode){
        case BillMode.Upcoming:
            return {};
        case BillMode.Now:
            {
                const today = startOfDay(now);
                return {
                    maxDate: today,
                    minDate: earliestBillableDate
                };
            }
        case BillMode.FutureDate:
            {
                return {
                    minDate: max(compact([
                        effectiveDate,
                        addDays(startOfDay(now), 1)
                    ])),
                    maxDate: getMaxFutureInvoiceDate(now)
                };
            }
    }
}
export function getDefaultIssueDate(mode, invoiceDateInfo, now, effectiveDate, earliestBillableDate) {
    switch(mode){
        case BillMode.Now:
            return startOfDay(now);
        case BillMode.Upcoming:
            return (invoiceDateInfo === null || invoiceDateInfo === void 0 ? void 0 : invoiceDateInfo.invoiceDraftDates.dates().length) ? invoiceDateInfo === null || invoiceDateInfo === void 0 ? void 0 : invoiceDateInfo.invoiceDraftDates.dates()[0] : startOfDay(now);
        case BillMode.FutureDate:
            {
                if (invoiceDateInfo && !invoiceDateInfo.isUpcomingSelected) {
                    return invoiceDateInfo.selectedDate;
                } else {
                    const { minDate } = getDateRange(mode, now, effectiveDate, earliestBillableDate);
                    if (!minDate) {
                        const error = new Error('no default issueDate');
                        sendErrReport(error);
                        throw error;
                    }
                    return minDate;
                }
            }
    }
}
export function normalizeEffectiveDate(effectiveDate) {
    return !effectiveDate ? null : effectiveDate instanceof Date ? effectiveDate : timeModel.fromDateStampObject(effectiveDate);
}
export function getBillModeOptions(params) {
    const { now, effectiveDate, invoiceDateInfo, billModeTexts } = params;
    return compact([
        {
            value: BillMode.Upcoming,
            text: billModeTexts.addToUpcomingInvoice(),
            disabled: !invoiceDateInfo,
            tooltip: !invoiceDateInfo ? billModeTexts.noUpcomingInvoice() : undefined
        },
        !relationshipModel.isFutureRelationship({
            effectiveDate
        }, now) && {
            value: BillMode.Now,
            text: billModeTexts.billNow()
        },
        {
            value: BillMode.FutureDate,
            text: billModeTexts.billOnDate()
        }
    ]);
}
export function getDefaultBillMode(billModeOptions, invoiceDateInfo) {
    if ((invoiceDateInfo === null || invoiceDateInfo === void 0 ? void 0 : invoiceDateInfo.isUpcomingSelected) && hasEnabledBillModeOption(billModeOptions, BillMode.Upcoming)) {
        return BillMode.Upcoming;
    } else if (invoiceDateInfo && !invoiceDateInfo.isUpcomingSelected && hasEnabledBillModeOption(billModeOptions, BillMode.FutureDate)) {
        return BillMode.FutureDate;
    } else if (hasEnabledBillModeOption(billModeOptions, BillMode.Now)) {
        return BillMode.Now;
    } else {
        const firstEnabledOption = billModeOptions.find((opt)=>!opt.disabled);
        if (!firstEnabledOption) {
            const error = new Error('no enabled billMode options');
            sendErrReport(error);
            throw error;
        }
        return firstEnabledOption.value;
    }
}
function hasEnabledBillModeOption(billModeOptions, billMode) {
    return billModeOptions.some((opt)=>opt.value === billMode && !opt.disabled);
}
export const deriveBillMode = (params)=>{
    var _ref;
    const { date, invoiceDateInfo, earliestBillableDate, now } = params;
    const today = startOfDay(now);
    const pickedDay = startOfDay(date);
    const draftDates = (_ref = invoiceDateInfo === null || invoiceDateInfo === void 0 ? void 0 : invoiceDateInfo.invoiceDraftDates.dates()) !== null && _ref !== void 0 ? _ref : [];
    if (pickedDay < today && pickedDay >= earliestBillableDate) {
        return BillMode.Now;
    }
    if (draftDates.some((draftDate)=>isSameDay(pickedDay, draftDate))) {
        return BillMode.Upcoming;
    }
    if (pickedDay > today) {
        return BillMode.FutureDate;
    }
    return BillMode.Now;
};
